import { computed, reactive } from 'vue'
import { handleSessionExpired } from '@/events/sessionExpiry'

export const SESSION_TIMEOUT_STORAGE_KEY = 'sessionTimeout'

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart']
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

let timer = null
let initialized = false
let pendingActivity = false
let heartbeatInFlight = false

export const sessionTimeoutState = reactive({
  enabled: true,
  sessionStartedAt: null,
  lastActivityAt: null,
  idleExpiresAt: null,
  absoluteExpiresAt: null,
  idleSeconds: 30 * 60,
  absoluteSeconds: 8 * 60 * 60,
  warningSeconds: 15 * 60,
  activitySyncSeconds: 60,
  remainingSeconds: 0,
  warningVisible: false,
  expirationKind: 'idle',
  extending: false,
})

export const formattedRemainingTime = computed(() => {
  const seconds = Math.max(0, sessionTimeoutState.remainingSeconds)
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainder = seconds % 60

  return [hours, minutes, remainder].map(value => String(value).padStart(2, '0')).join(':')
})

const toStoredState = () => ({
  enabled: sessionTimeoutState.enabled,
  sessionStartedAt: sessionTimeoutState.sessionStartedAt,
  lastActivityAt: sessionTimeoutState.lastActivityAt,
  idleExpiresAt: sessionTimeoutState.idleExpiresAt,
  absoluteExpiresAt: sessionTimeoutState.absoluteExpiresAt,
  idleSeconds: sessionTimeoutState.idleSeconds,
  absoluteSeconds: sessionTimeoutState.absoluteSeconds,
  warningSeconds: sessionTimeoutState.warningSeconds,
  activitySyncSeconds: sessionTimeoutState.activitySyncSeconds,
})

const normalizeMetadata = metadata => ({
  enabled: metadata.enabled ?? true,
  sessionStartedAt: metadata.session_started_at ?? metadata.sessionStartedAt,
  lastActivityAt: metadata.last_activity_at ?? metadata.lastActivityAt,
  idleExpiresAt: metadata.idle_expires_at ?? metadata.idleExpiresAt,
  absoluteExpiresAt: metadata.absolute_expires_at ?? metadata.absoluteExpiresAt,
  idleSeconds: Number(metadata.idle_seconds ?? metadata.idleSeconds ?? sessionTimeoutState.idleSeconds),
  absoluteSeconds: Number(metadata.absolute_seconds ?? metadata.absoluteSeconds ?? sessionTimeoutState.absoluteSeconds),
  warningSeconds: Number(metadata.warning_seconds ?? metadata.warningSeconds ?? sessionTimeoutState.warningSeconds),
  activitySyncSeconds: Number(metadata.activity_sync_seconds ?? metadata.activitySyncSeconds ?? sessionTimeoutState.activitySyncSeconds),
})

const validDeadline = value => value && !Number.isNaN(Date.parse(value))

const persist = () => {
  if (!localStorage.getItem('token'))
    return

  localStorage.setItem(SESSION_TIMEOUT_STORAGE_KEY, JSON.stringify(toStoredState()))
}

const evaluate = () => {
  if (!localStorage.getItem('token') || !sessionTimeoutState.enabled)
    return

  if (!validDeadline(sessionTimeoutState.idleExpiresAt) || !validDeadline(sessionTimeoutState.absoluteExpiresAt))
    return

  const idleDeadline = Date.parse(sessionTimeoutState.idleExpiresAt)
  const absoluteDeadline = Date.parse(sessionTimeoutState.absoluteExpiresAt)
  const deadline = Math.min(idleDeadline, absoluteDeadline)
  const millisecondsRemaining = deadline - Date.now()

  sessionTimeoutState.expirationKind = absoluteDeadline <= idleDeadline ? 'absolute' : 'idle'
  sessionTimeoutState.remainingSeconds = Math.max(0, Math.ceil(millisecondsRemaining / 1000))
  sessionTimeoutState.warningVisible = millisecondsRemaining > 0
    && millisecondsRemaining <= sessionTimeoutState.warningSeconds * 1000

  if (millisecondsRemaining <= 0) {
    sessionTimeoutState.warningVisible = false
    handleSessionExpired({
      title: 'Your login session has expired.',
      message: sessionTimeoutState.expirationKind === 'absolute'
        ? 'Your session reached its maximum lifetime. Please log in again.'
        : 'Your session expired due to inactivity. Please log in again.',
    })
  }
}

export const syncSessionTimeout = metadata => {
  if (!metadata)
    return

  Object.assign(sessionTimeoutState, normalizeMetadata(metadata))
  persist()
  evaluate()
}

export const syncSessionFromResponse = response => {
  const headers = response?.headers
  const getHeader = name => headers?.get?.(name) ?? headers?.[name.toLowerCase()]
  const sessionStartedAt = getHeader('X-Session-Started-At')

  if (!sessionStartedAt)
    return

  syncSessionTimeout({
    'session_started_at': sessionStartedAt,
    'last_activity_at': getHeader('X-Session-Last-Activity-At'),
    'idle_expires_at': getHeader('X-Session-Idle-Expires-At'),
    'absolute_expires_at': getHeader('X-Session-Absolute-Expires-At'),
    'warning_seconds': getHeader('X-Session-Warning-Seconds'),
    'activity_sync_seconds': getHeader('X-Session-Activity-Sync-Seconds'),
  })
}

export const shouldMarkRequestAsActivity = () => {
  if (!pendingActivity)
    return false

  pendingActivity = false

  return true
}

const requestExtension = async () => {
  const token = localStorage.getItem('token')
  if (!token || heartbeatInFlight)
    return

  heartbeatInFlight = true
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const response = await fetch(`${API_BASE_URL}/auth/session/extend`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
      },
    })

    const body = await response.json().catch(() => null)

    if (response.status === 401) {
      handleSessionExpired({ message: body?.message })

      return
    }

    if (response.ok)
      syncSessionTimeout(body?.data?.session)
  }
  finally {
    heartbeatInFlight = false
  }
}

const recordActivity = () => {
  if (!localStorage.getItem('token') || sessionTimeoutState.extending)
    return

  pendingActivity = true

  const lastSync = Date.parse(sessionTimeoutState.lastActivityAt || 0)
  const syncInterval = sessionTimeoutState.activitySyncSeconds * 1000

  if (!lastSync || Date.now() - lastSync >= syncInterval)
    void requestExtension()
}

const resetRuntimeState = () => {
  sessionTimeoutState.warningVisible = false
  sessionTimeoutState.remainingSeconds = 0
  sessionTimeoutState.sessionStartedAt = null
  sessionTimeoutState.lastActivityAt = null
  sessionTimeoutState.idleExpiresAt = null
  sessionTimeoutState.absoluteExpiresAt = null
  pendingActivity = false
}

export const stayLoggedIn = async () => {
  sessionTimeoutState.extending = true
  try {
    await requestExtension()
  }
  finally {
    sessionTimeoutState.extending = false
  }
}

export const logoutNow = async () => {
  const token = localStorage.getItem('token')
  if (token) {
    void fetch(`${API_BASE_URL}/auth/logout`, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {})
  }

  localStorage.removeItem('token')
  localStorage.removeItem('userData')
  localStorage.removeItem(SESSION_TIMEOUT_STORAGE_KEY)
  resetRuntimeState()
  window.location.href = '/login'
}

export const initializeSessionTimeout = () => {
  if (initialized)
    return

  initialized = true

  const stored = localStorage.getItem(SESSION_TIMEOUT_STORAGE_KEY)
  if (stored) {
    try {
      syncSessionTimeout(JSON.parse(stored))
    }
    catch {
      localStorage.removeItem(SESSION_TIMEOUT_STORAGE_KEY)
    }
  }

  ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, recordActivity, { passive: true }))
  window.addEventListener('storage', event => {
    if (event.key === SESSION_TIMEOUT_STORAGE_KEY && event.newValue) {
      try {
        Object.assign(sessionTimeoutState, normalizeMetadata(JSON.parse(event.newValue)))
        evaluate()
      }
      catch {
        // Ignore malformed storage written outside this application.
      }
    }

    if (event.key === 'token' && !event.newValue)
      resetRuntimeState()
  })
  window.addEventListener('session-auth-cleared', resetRuntimeState)

  timer = window.setInterval(evaluate, 1000)
  evaluate()
}

export const stopSessionTimeout = () => {
  if (timer)
    window.clearInterval(timer)
  timer = null
}

export const useSessionTimeout = () => ({
  state: sessionTimeoutState,
  formattedRemainingTime,
  initialize: initializeSessionTimeout,
  stayLoggedIn,
  logoutNow,
})

export const sessionTimeout = useSessionTimeout()
